import React, { useState } from 'react';
import {
  Alert,
  Box,
  Divider,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
  IconButton,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  QrCode as QrCodeIcon,
  Refresh as RefreshIcon,
  PhotoCamera as PhotoCameraIcon,
  CameraAlt as CameraAltIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { productService } from '../services/productService';
import { fileUrl } from '../services/api';
import { PILE_CHASSE_FIXE } from '../theme/jetons';
import { useLanguage } from '../contexts/LanguageContext';
import ZXingBarcodeScanner from './ZXingBarcodeScanner';
import PhotoCapture from './PhotoCapture';

const ProductForm = ({ product, suppliers, onSubmit, onCancel, loading, onAjuster, onSupprimer }) => {
  const { t } = useLanguage();
  const [generatingBarcode, setGeneratingBarcode] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  // 🔴 L'ERREUR D'UPLOAD A SON PROPRE ÉTAT : elle était `console.error` seule.
  const [erreurImage, setErreurImage] = useState(null);
  // 🔴 ET LE CHEMIN RENDU PAR LE SERVEUR AUSSI — sans lui, un upload réussi
  // laissait l'aperçu sur « Aucune photo », donc prouvait son propre échec.
  const [cheminImage, setCheminImage] = useState(null);

  /**
   * L'image À AFFICHER, en une seule expression.
   *
   * ⚠️ L'ORDRE EST UNE PRIORITÉ, PAS UN HASARD : ce que le SERVEUR vient de
   * rendre l'emporte sur ce que le produit portait au chargement. Sans ce
   * premier terme, un upload réussi laissait l'aperçu sur l'ancienne valeur —
   * donc sur « Aucune photo » pour un produit qui n'en avait pas encore.
   * ⚠️ Le fichier LOCAL (produit neuf) vient en dernier : il n'existe que tant
   * que rien n'a été envoyé.
   */
  const imageAffichee = cheminImage || product?.image_path || null;
  const fichierLocal = selectedImage || capturedImage || null;

  /* 🔴 LES CINQ DERNIERS MOUVEMENTS S'EN VONT — RECTIFICATION D'UNE
     JUSTIFICATION QUI ÉTAIT FAUSSE, ET QUI A CIRCULÉ TROIS FOIS.
     Ce commentaire affirmait que le panneau était « faux dès le 6ᵉ mouvement ».
     ⚠️ MESURÉ APRÈS COUP : il ne l'était pas. Il chargeait les 5 dernières
     entrées ET les 5 dernières sorties, fusionnait et coupait à 5 — et sur
     20 000 tirages aléatoires cette méthode ne diverge JAMAIS de la vraie
     liste à une profondeur de 5. La raison est arithmétique : les 5 premiers
     ne peuvent contenir plus de 5 entrées ni plus de 5 sorties, donc ils sont
     toujours dans les deux fenêtres chargées. Le panneau affichait 5. Il était
     EXACT.
     🟢 LA VRAIE RAISON DU DÉPART, ET ELLE SUFFIT : cette méthode PLAFONNE à 10
     mouvements, dont 5 au plus par famille. Au-delà elle ne se trompe pas —
     elle ne peut rien rendre. La vue détail veut un historique COMPLET et
     PAGINÉ, ce qui est hors de portée de deux requêtes fusionnées côté client,
     quelle que soit leur écriture. Elle est donc reconstruite au lot RU3-d sur
     `GET /products/<id>/movements`, qui trie et pagine sur l'ENSEMBLE.
     (Et un formulaire d'édition sert à modifier, pas à consulter — mais ça,
     c'était l'argument de forme, pas celui qui décide.) */

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: product || {
      name: '',
      barcode: '',
      price: '',
      current_stock: '',
      min_stock: '',
      supplier_id: '',
      comment: '',
    },
  });

  const handleGenerateBarcode = async () => {
    try {
      setGeneratingBarcode(true);
      const response = await productService.generateBarcode();
      setValue('barcode', response.barcode);
    } catch (err) {
      console.error('Erreur lors de la génération du code-barres:', err);
    } finally {
      setGeneratingBarcode(false);
    }
  };

  /**
   * 🔴 DEUX DÉFAUTS DISTINCTS FAISAIENT DIRE « L'UPLOAD NE MARCHE PAS », ET
   * AUCUN DES DEUX NE SE SIGNALAIT.
   *
   *   1. **L'erreur était AVALÉE.** Le `catch` faisait un `console.error` et
   *      rien d'autre : pas de message, pas d'état d'erreur. Un fichier trop
   *      lourd, un type refusé, un jeton expiré — l'écran restait muet et
   *      identique. *Une erreur qui ne sort pas de la console n'existe pas
   *      pour l'utilisateur.*
   *   2. **Et même en cas de SUCCÈS, rien ne rechargeait le produit.** Le
   *      serveur écrivait `image_path`, mais l'objet `product` du formulaire
   *      restait celui d'avant : l'aperçu continuait d'afficher « Aucune
   *      photo ». **L'upload réussissait, et l'écran prouvait le contraire.**
   *
   * ⚠️ *Deux causes, un seul symptôme — et la seconde est la plus trompeuse :
   * elle transforme un succès en preuve d'échec.*
   *
   * ⚠️ LE CHEMIN DU PRODUIT NEUF ÉTAIT DÉJÀ CORRECT et ne change pas :
   * l'image voyage dans `formData.selectedImage`, et `Products.js` l'envoie
   * après création **avec** son message d'erreur. C'est le chemin du produit
   * EXISTANT qui n'en avait pas.
   */
  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    // ⚠️ Le champ est vidé TOUT DE SUITE : sans ça, choisir deux fois le même
    // fichier n'émet aucun `change` — le navigateur voit la même valeur.
    event.target.value = '';
    setErreurImage(null);

    try {
      setUploadingImage(true);

      if (product?.id) {
        const maj = await productService.uploadProductImage(product.id, file);
        // 🔴 LE CHEMIN RENDU PAR LE SERVEUR FAIT FOI, et il est porté par un
        // état LOCAL plutôt que par un rechargement de la liste : le
        // formulaire est ouvert, ses champs sont en cours d'édition, et les
        // recharger perdrait ce que l'utilisateur vient de taper.
        setCheminImage(maj?.image_path || maj?.product?.image_path || null);
      } else {
        setSelectedImage(file);
      }
    } catch (err) {
      // ⚠️ LE MESSAGE DU SERVEUR D'ABORD — il dit « fichier trop volumineux »
      // ou « type non autorisé », ce qu'un libellé générique ne dira jamais.
      setErreurImage(err?.response?.data?.error || t('messages.imageUploadFailed'));
    } finally {
      setUploadingImage(false);
    }
  };

  const handleScanBarcode = () => {
    setScannerOpen(true);
  };

  const handleBarcodeScanned = (barcode) => {
    setValue('barcode', barcode);
    setScannerOpen(false);
  };

  const handleScannerError = (error) => {
    console.error('Erreur du scanner:', error);
    setScannerOpen(false);
  };

  const handleTakePhoto = () => {
    setCameraOpen(true);
  };

  const handlePhotoCaptured = (imageBlob) => {
    setCapturedImage(imageBlob);
    setSelectedImage(imageBlob);
    setCameraOpen(false);
  };

  const handleCameraClose = () => {
    setCameraOpen(false);
  };

  /**
   * 🔴 LA CHARGE SE CONSTRUIT PAR ÉNUMÉRATION, JAMAIS PAR RECOPIE.
   *
   * Elle s'écrivait `{...data, …}`. Or `data` n'est pas la saisie : c'est le
   * `defaultValues` du formulaire, **et `defaultValues` est l'objet PRODUIT
   * servi par l'API**. Le `PUT` renvoyait donc au serveur tout ce qu'il en
   * avait reçu — mesuré dans le corps de la requête :
   *
   *   {"id":7,"created_at":"2026-07-31T…","updated_at":"2026-08-22T…",
   *    "stock_id":1,"stock_name":"Stock Principal","supplier_name":"TechSupply
   *    Pro","is_low_stock":false,"current_stock":12, …}
   *
   * ⚠️ `is_low_stock` est le champ CALCULÉ par le serveur que la décision 10
   * nous a fait cesser de recopier — et le formulaire le lui réexpédiait. La
   * route en ignore la plupart, ce qui rendait tout cela inoffensif *par
   * accident* : chaque clé nouvellement acceptée par le serveur deviendrait
   * une écriture que personne n'a demandée.
   *
   * 🔴 ET `current_stock`, LUI, N'ÉTAIT PAS IGNORÉ — c'est le défaut mesuré au
   * navigateur, trois fois de suite. `useForm` lit `defaultValues` **au
   * montage et une seule fois** (react-hook-form 7.60 : `_formValues` est un
   * clone profond, seule la prop `values`, non employée ici, resynchronise).
   * Depuis RU3-c on peut ajuster SANS quitter la fiche : le champ affiché lit
   * `product.current_stock` en direct et montrait 22, pendant que le
   * formulaire tenait encore le 12 du montage. Enregistrer réécrivait 12.
   *
   *   entrée +10 à 19:41:43 → écriture produit à 19:41:51 → stock 12
   *   entrée +10 à 19:43:13 → écriture produit à 19:43:17 → stock 12
   *
   * L'ajustement était annulé **en silence**, et l'écran venait d'annoncer sa
   * réussite. 30 unités entrées, 0 sortie, stock affiché 12.
   *
   * ⚠️ LE CORRECTIF N'EST PAS UN `reset()`. Remettre le formulaire à jour
   * ferait écrire la BONNE valeur — et laisserait intact le vrai défaut : ce
   * formulaire n'est plus la source du stock, il n'a donc pas à l'écrire. Le
   * stock ne se décrète pas, il RÉSULTE des mouvements.
   *
   * 🟢 À LA CRÉATION, EN REVANCHE, IL L'ENVOIE — et c'est cohérent, pas une
   * exception : à la création il n'y a aucun mouvement à contredire. C'est le
   * point de départ, la seule valeur de stock que ce formulaire possède.
   */
  /**
   * L'intitulé d'une section : un filet et un mot.
   *
   * ⚠️ `overline` ET NON `subtitle2` : le rôle de ces deux mots est de SÉPARER,
   * pas d'annoncer. Un intitulé aussi lourd qu'un libellé de champ entrerait en
   * concurrence avec les champs qu'il regroupe — c'est la même retenue que le
   * compteur d'`EnteteEcran`, qui suit le titre sans le concurrencer.
   */
  const titreSection = (libelle) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
      <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
        {libelle}
      </Typography>
      <Divider sx={{ flex: 1 }} />
    </Box>
  );

  const onSubmitForm = (data) => {
    const formData = {
      name: data.name,
      barcode: data.barcode,
      price: parseFloat(data.price) || 0,
      min_stock: parseInt(data.min_stock) || 0,
      supplier_id: data.supplier_id || null,
      comment: data.comment,
    };

    if (!product) {
      formData.current_stock = parseInt(data.current_stock) || 0;
      // L'image n'accompagne la charge qu'à la création : en édition elle est
      // déjà envoyée à part, dès la sélection (`handleImageUpload`).
      formData.selectedImage = selectedImage;
    }

    onSubmit(formData);
  };

  /* 🔴 `pt` SUR LE FORMULAIRE, ET CE N'EST PAS UN RÉGLAGE D'ESPACEMENT : SANS
     LUI LE PREMIER LIBELLÉ EST COUPÉ. `<Grid container spacing={3}>` pose
     `margin-top: -24px`, que le `padding-top: 24px` du premier `<Grid item>`
     annule exactement — la bordure haute du premier champ atterrit donc à y=0
     du formulaire. Or un libellé FLOTTANT de MUI se dessine AU-DESSUS de cette
     bordure (`translateY(-9px)`) : il sort de la zone, et `DialogContent`, qui
     gère son propre débordement, le rogne.
     ⚠️ Le défaut ne se voit que sur la PREMIÈRE rangée : ailleurs, le
     `padding-top` de la rangée précédente laisse la place. C'est ce qui l'a
     laissé passer. */
  return (
    <Box component="form" onSubmit={handleSubmit(onSubmitForm)} sx={{ pt: 1.5 }}>
      <Grid container spacing={3}>
        {/* ══ IDENTITÉ — la vignette à GAUCHE, le nom et le code à droite ══
            🔴 LA PHOTO ÉTAIT UN ENCADRÉ À DROITE qui occupait autant de place
            que le fournisseur pour dire « vous pouvez ajouter une image ». Une
            vignette carrée dit la même chose en la MONTRANT, et rend la place
            aux deux champs qui identifient le produit. */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <Box sx={{ width: 160, flexShrink: 0 }}>
              {/* ⚠️ LA VIGNETTE OCCUPE SA PLACE MÊME VIDE — même raison qu'à la
                  fiche détail : un emplacement qui n'apparaît qu'une fois rempli
                  fait sauter la mise en page au premier envoi, et personne ne
                  devine qu'on POUVAIT en mettre une.
                  🟢 ET LES DEUX BOUTONS RESTENT ACTIFS À LA CRÉATION. Mesuré
                  avant d'écrire : `handleImageUpload` garde le fichier dans
                  `selectedImage` quand le produit n'a pas encore d'id, et
                  `handleCreateProduct` l'envoie APRÈS la création. Rien n'est
                  inerte — proposer l'action est donc honnête. */}
              <Box
                sx={{
                  width: 160, height: 160, borderRadius: 1,
                  border: '1px dashed', borderColor: 'divider',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  overflow: 'hidden', mb: 1,
                }}
              >
                {(imageAffichee || fichierLocal) ? (
                  <Box
                    component="img"
                    src={imageAffichee
                      ? fileUrl(imageAffichee)
                      : URL.createObjectURL(fichierLocal)}
                    alt={product?.name || t('products.imageTitle')}
                    onClick={() => setImagePreviewOpen(true)}
                    title={t('products.clickToView')}
                    sx={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }}
                  />
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    {t('products.noPhotoShort')}
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<PhotoCameraIcon />}
                  disabled={uploadingImage || loading}
                  size="small"
                  fullWidth
                >
                  {uploadingImage ? t('products.uploading') : t('products.file')}
                  <input type="file" hidden accept="image/*" onChange={handleImageUpload} />
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<CameraAltIcon />}
                  onClick={handleTakePhoto}
                  disabled={loading}
                  size="small"
                  fullWidth
                >
                  {t('products.camera')}
                </Button>
              </Box>

              {/* 🔴 L'ERREUR SORT DE LA CONSOLE. Elle y restait seule : un
                  fichier trop lourd, un type refusé ou un jeton expiré
                  laissaient l'écran muet et identique — et l'utilisateur
                  concluait « l'upload ne marche pas », sans savoir pourquoi. */}
              {erreurImage && (
                <Alert severity="error" onClose={() => setErreurImage(null)}
                       sx={{ mt: 1 }}>
                  {erreurImage}
                </Alert>
              )}
            </Box>

            <Box sx={{ flex: '1 1 320px', display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Controller
                name="name"
                control={control}
                rules={{ required: t('products.nameRequired') }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={t('products.productName')}
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    disabled={loading}
                  />
                )}
              />
              {/* ⚠️ L'ICÔNE DE SCAN EST DÉJÀ DANS LE CHAMP — mesuré avant
                  d'écrire : la maquette demandait ce qui existait. Elle est en
                  `endAdornment`, avec celle qui engendre un code. Ce qui restait
                  à retirer, c'est l'enveloppe `display: flex` autour du champ :
                  elle datait d'un temps où un bouton lui tenait compagnie, et
                  elle n'entoure plus qu'un seul enfant. */}
              <Controller
                name="barcode"
                control={control}
                rules={{ required: t('products.barcodeRequired') }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={t('products.barcode')}
                    sx={{ '& .MuiInputBase-input': { fontFamily: PILE_CHASSE_FIXE } }}
                    error={!!errors.barcode}
                    helperText={errors.barcode?.message}
                    disabled={loading}
                    InputProps={{
                      endAdornment: (
                        <Box display="flex" gap={1}>
                          <IconButton
                            size="small"
                            onClick={handleScanBarcode}
                            disabled={loading}
                          >
                            <QrCodeIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={handleGenerateBarcode}
                            disabled={generatingBarcode || loading}
                          >
                            <RefreshIcon />
                          </IconButton>
                        </Box>
                      ),
                    }}
                  />
                )}
              />
            </Box>
          </Box>
        </Grid>

        {/* ⚠️ LE FOURNISSEUR PREND SA PROPRE LIGNE. Il partageait la sienne avec
            l'encadré d'image — deux choses sans rapport, à largeur égale. */}
        <Grid item xs={12}>
          <Controller
            name="supplier_id"
            control={control}
            rules={{ 
              required: t('products.supplierRequired')
            }}
            render={({ field }) => (
              <FormControl fullWidth error={!!errors.supplier_id}>
                <InputLabel>{t('products.supplierLabel')}</InputLabel>
                <Select
                  {...field}
                  label={t('products.supplierLabel')}
                  disabled={loading}
                >
                  <MenuItem value="">
                    <em>{t('products.selectSupplier')}</em>
                  </MenuItem>
                  {suppliers.map((supplier) => (
                    <MenuItem key={supplier.id} value={supplier.id}>
                      {supplier.company_name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.supplier_id && (
                  <Typography variant="caption" color="error">
                    {errors.supplier_id.message}
                  </Typography>
                )}
              </FormControl>
            )}
          />
        </Grid>

        {/* 🔴 DEUX SECTIONS NOMMÉES, ET CE N'EST PAS DE L'ORNEMENT. Prix, stock
            et seuil étaient sur la MÊME ligne alors qu'ils ne répondent pas à la
            même question : « combien en ai-je » et « combien coûte-t-il » sont
            deux sujets, et les aligner invitait à les lire comme un seul. */}
        <Grid item xs={12}>{titreSection(t('products.sectionStock'))}</Grid>
        {/* 🔴 LE STOCK NE SE TAPE PLUS — IL S'AJUSTE, ET LA DIFFÉRENCE EST
            UNE TRACE. Écrire « 42 » dans ce champ appelait
            `PUT /products/<id>`, qui pose `current_stock` directement :
            aucune entrée, aucune sortie, AUCUN MOUVEMENT. C'est l'un des
            trois écrivains muets relevés en écrivant la route d'historique
            — et le seul des trois qu'un utilisateur déclenche à la main,
            tous les jours. Le stock changeait et l'historique l'ignorait.
            Le lien ouvre le dialogue de RU3-b : même geste, mais il passe
            par une entrée ou une sortie, donc il laisse une ligne.

            ⚠️ ET SEULEMENT EN ÉDITION. À la CRÉATION il n'y a pas encore de
            produit à ajuster : le champ reste saisissable, c'est le stock
            initial. Griser un champ qu'aucun lien ne remplace empêcherait
            simplement de créer un produit avec du stock. */}
        {/* ⚠️ LES DEUX CHAMPS D'UNE SECTION RESTENT À PORTÉE D'ŒIL. En `md={6}`
            sur un dialogue `maxWidth="lg"`, ils étaient poussés aux deux bouts
            de l'écran : « Seuil d'alerte » se lit AVEC « Stock actuel », les
            séparer d'un demi-mètre les rend étrangers l'un à l'autre. Une
            section se lit comme un GROUPE, pas comme deux champs isolés. */}
        {/* ⚠️ LES DEUX CHAMPS D'UNE SECTION RESTENT À PORTÉE D'ŒIL, ET SUR LA
            MÊME LIGNE. En `md={6}` sur un dialogue `maxWidth="lg"` ils étaient
            poussés aux deux bouts de l'écran : « Seuil d'alerte » se lit AVEC
            « Stock actuel », les séparer d'un demi-mètre les rend étrangers
            l'un à l'autre. Une section se lit comme un GROUPE.
            🟢 Et un conteneur BORNÉ plutôt que deux colonnes de grille : la
            grille répartit la largeur DISPONIBLE, qui est ici bien plus grande
            que ce dont deux nombres ont besoin. */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', maxWidth: 620 }}>
            <Box sx={{ flex: '1 1 260px' }}>
            {product ? (
              <TextField
                fullWidth
                label={t('products.currentStock')}
                value={product.current_stock ?? 0}
                disabled
                helperText={onAjuster ? (
                  <Link
                    component="button"
                    type="button"
                    variant="caption"
                    onClick={onAjuster}
                  >
                    {t('products.adjust')}
                  </Link>
                ) : ' '}
              />
            ) : (
              <Controller
                name="current_stock"
                control={control}
                rules={{
                  required: t('products.currentStockRequired'),
                  min: { value: 0, message: t('products.currentStockPositive') }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={t('products.initialStock')}
                    type="number"
                    inputProps={{ min: 0 }}
                    error={!!errors.current_stock}
                    helperText={errors.current_stock?.message}
                    disabled={loading}
                  />
                )}
              />
            )}
            </Box>
            <Box sx={{ flex: '1 1 260px' }}>
            <Controller
              name="min_stock"
              control={control}
              rules={{ 
                required: t('products.minStockRequired'),
                min: { value: 0, message: t('products.minStockPositive') }
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label={t('products.alertThreshold')}
                  type="number"
                  inputProps={{ min: 0 }}
                  error={!!errors.min_stock}
                  helperText={errors.min_stock?.message}
                  disabled={loading}
                />
              )}
            />
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12}>{titreSection(t('products.sectionPricing'))}</Grid>
        <Grid item xs={12} sx={{ maxWidth: 300 }}>
          <Controller
            name="price"
            control={control}
            rules={{ 
              required: t('products.priceRequired'),
              min: { value: 0, message: t('products.pricePositive') }
            }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label={t('products.purchasePrice')}
                type="number"
                inputProps={{ step: 0.01, min: 0 }}
                error={!!errors.price}
                helperText={errors.price?.message || t('products.priceHelp')}
                disabled={loading}
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Controller
            name="comment"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label={`${t('products.comment')} (${t('products.optional')})`}
                multiline
                rows={3}
                disabled={loading}
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          {/* 🔴 « SUPPRIMER » EST À GAUCHE, ET C'EST LA DISTANCE QUI
              COMPTE, PAS LA COULEUR. Une action irréversible posée à côté
              d'« Enregistrer » se clique par erreur de trajectoire : le
              rouge prévient APRÈS que la souris est partie. Le
              `space-between` met toute la largeur du formulaire entre les
              deux.
              ⚠️ ABSENT, PAS GRISÉ, quand l'utilisateur n'a pas le droit de
              supprimer. `Products.js` décide (`canDelete`), ce composant
              obéit : `onSupprimer` vaut alors `null`. Même raison qu'au
              « Combler » de RU3-b — un bouton grisé fait chercher pourquoi. */}
          {/* ⚠️ `space-between` N'A DE SENS QU'À DEUX GROUPES. À la création — et
              pour qui n'a pas le droit de supprimer — le groupe de gauche est
              VIDE : la barre garderait toute la largeur pour ne rien y mettre,
              et les deux boutons paraîtraient exilés. On aligne alors à
              droite. */}
          <Box
            display="flex"
            gap={2}
            justifyContent={product && onSupprimer ? 'space-between' : 'flex-end'}
          >
            <Box>
              {product && onSupprimer && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={onSupprimer}
                  disabled={loading}
                >
                  {t('common.delete')}
                </Button>
              )}
            </Box>
            <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              onClick={onCancel}
              disabled={loading}
            >
            {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
            >
            {loading ? t('products.saving') : (product ? t('products.saveChanges') : t('products.create'))}
            </Button>
            </Box>
          </Box>
        </Grid>
      </Grid>
      {/* Scanner de code-barres et capture photo restent en dehors du layout principal */}
      <ZXingBarcodeScanner
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onScan={handleBarcodeScanned}
        onError={handleScannerError}
      />
      <PhotoCapture
        open={cameraOpen}
        onClose={handleCameraClose}
        onCapture={handlePhotoCaptured}
      />

      {/* Modal de visualisation de l'image */}
      <Dialog
        open={imagePreviewOpen}
        onClose={() => setImagePreviewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{t('products.imageTitle')}</Typography>
            <Button onClick={() => setImagePreviewOpen(false)} size="small">
              <CloseIcon />
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          {(imageAffichee || fichierLocal) && (
            <Box
              component="img"
              src={
                imageAffichee
                  ? fileUrl(imageAffichee)
                  : URL.createObjectURL(fichierLocal)
              }
              alt={product?.name || 'Produit'}
              sx={{
                width: '100%',
                height: 'auto',
                maxHeight: '70vh',
                objectFit: 'contain',
                borderRadius: 1
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ProductForm; 